import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore, UserRole } from '@dukene/shared';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

const otpSchema = z.object({
  code: z.string().length(6),
});

type LoginFormData = z.infer<typeof loginSchema>;
type OTPFormData = z.infer<typeof otpSchema>;

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const verify2FA = useAuthStore((state) => state.verify2FA);
  const resend2FA = useAuthStore((state) => state.resend2FA);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [step, setStep] = useState<'login' | '2fa'>('login');
  const [loginEmail, setLoginEmail] = useState('');

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const otpForm = useForm<OTPFormData>({
    resolver: zodResolver(otpSchema),
  });

  const onLoginSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      const result = await login(data.email, data.password);

      if (result?.requires2FA) {
        setLoginEmail(data.email);
        setStep('2fa');
        return;
      }

      const user = result;
      if (!user || (user.role !== UserRole.ADMIN && user.role !== UserRole.SUPER_ADMIN)) {
        useAuthStore.getState().logout();
        return;
      }

      navigate('/');
    } catch (err: any) {
      // Silently fail for security
    } finally {
      setIsLoading(false);
    }
  };

  const onOTPSubmit = async (data: OTPFormData) => {
    try {
      setIsLoading(true);
      const user = await verify2FA(loginEmail, data.code);

      if (!user || (user.role !== UserRole.ADMIN && user.role !== UserRole.SUPER_ADMIN)) {
        useAuthStore.getState().logout();
        return;
      }

      navigate('/');
    } catch (err: any) {
      // Silently fail for security
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsResending(true);
      setResendStatus('idle');
      await resend2FA(loginEmail);
      setResendStatus('success');
      setTimeout(() => setResendStatus('idle'), 3000);
    } catch (err) {
      setResendStatus('error');
      setTimeout(() => setResendStatus('idle'), 3000);
    } finally {
      setIsResending(false);
    }
  };

  const [otpValue, setOtpValue] = useState(['', '', '', '', '', '']);

  const handleOtpChange = (value: string, index: number) => {
    if (value && isNaN(Number(value))) return;

    const newOtp = [...otpValue];
    // Only take the last character if multiple are entered (e.g. from mobile keyboard suggestions)
    newOtp[index] = value ? value[value.length - 1] : '';
    setOtpValue(newOtp);

    // Sync with form field
    const code = newOtp.join('');
    otpForm.setValue('code', code, { shouldValidate: true });

    // Move to next input if value is entered
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }

    // Auto verify if 6 digits are entered
    if (code.length === 6 && !isLoading) {
      setIsLoading(true);
      otpForm.handleSubmit(onOTPSubmit)();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
    if (pastedData.every(char => !isNaN(Number(char)))) {
      const newOtp = [...otpValue];
      pastedData.forEach((char, i) => {
        if (i < 6) newOtp[i] = char;
      });
      setOtpValue(newOtp);
      const code = newOtp.join('');
      otpForm.setValue('code', code, { shouldValidate: true });
      document.getElementById(`otp-${Math.min(pastedData.length, 5)}`)?.focus();

      // Auto verify if 6 digits were pasted
      if (code.length === 6 && !isLoading) {
        setIsLoading(true);
        otpForm.handleSubmit(onOTPSubmit)();
      }
    }
  };

  if (step === '2fa') {
    return (
      <div className="w-full max-w-sm bg-white rounded p-8">
        <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-4 text-center">
          <div className="text-sm text-gray-400 mb-2">Check your email for the code</div>

          <div className="flex justify-between gap-2 py-2" onPaste={handlePaste}>
            {otpValue.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="w-10 h-12 border border-gray-300 rounded text-center text-xl font-bold focus:border-gray-800 focus:outline-none transition-colors"
                disabled={isLoading}
                autoFocus={index === 0}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          <input type="hidden" {...otpForm.register('code')} />

          <button
            type="submit"
            className="w-full py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 mt-4"
            disabled={isLoading || otpForm.getValues('code')?.length !== 6}
          >
            {isLoading ? '...' : 'Verify'}
          </button>

          <div className="flex flex-col gap-2 mt-4">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isResending || isLoading}
              className="text-xs text-blue-500 hover:text-blue-700 disabled:text-gray-400 font-medium"
            >
              {isResending ? 'Resending...' : resendStatus === 'success' ? 'Code resent!' : resendStatus === 'error' ? 'Failed to resend' : 'Resend code'}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('login');
                setOtpValue(['', '', '', '', '', '']);
                otpForm.reset();
              }}
              className="text-xs text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              Back to login
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm bg-white rounded p-8">
      <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
        <div>
          <input
            {...loginForm.register('email')}
            type="password"
            autoComplete="off"
            spellCheck="false"
            autoCapitalize="none"
            autoCorrect="off"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-400"
            disabled={isLoading}
          />
        </div>

        <div>
          <input
            {...loginForm.register('password')}
            type="password"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-400"
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          className="w-full py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? '...' : 'Submit'}
        </button>
      </form>
    </div>
  );
}
